import type {
  EnergyProfile, HealthMetricMap, IsoDate, LogMap, Measurement, QuestRank
} from "./types.js";
import type { Countdown, SeasonBaseline } from "./plan.js";
import type { WeightTrend } from "./measurements.js";
import type { EnergyGuide } from "./energy.js";
import type { ComplianceRates, StatusAssessment } from "./stats.js";
import { countdownInfo } from "./plan.js";
import { weightTrend } from "./measurements.js";
import { dayCompletion, questRank } from "./scoring.js";
import { compliance, statusAssessment } from "./stats.js";
import { DEFAULT_ENERGY_PROFILE, energyGuideFor } from "./energy.js";

/**
 * 戰報：那一份可截圖或複製的進度摘要。
 *
 * CONTEXT.md 說戰報「是呈現，不是新的計算」。先前那只是文件上的一句話——
 * 分享頁與「複製文字戰報」各自算了一份，而且已經漂開了：分享頁把 today 餵給
 * 預估與狀態判斷、卻把 planDate 餵給倒數與達標率，文字版則一律用 planDate。
 * 生日目標日之後兩邊的「11/4 預估」會是兩個不同的數字。
 *
 * 現在數字只有這一份，**日期只有一個參數**，那個歧異在型別上就不成立了。
 * 兩個呼叫端退成 adapter：一個畫成 JSX，一個 join 成文字。要顯示哪幾項、
 * 用什麼標籤、排什麼順序仍然各自決定——那是排版，不是計算。
 *
 * 與 src/domain/ 的其他模組一樣不讀系統時間：「今天」由呼叫端傳入。
 */

export interface BattleReportInput {
  measurements: Measurement[];
  logs: LogMap;
  healthMetrics: HealthMetricMap;
  /** 戰報的基準日。呼叫端傳夾進計畫期間的 planDate。 */
  today: IsoDate;
  profile?: EnergyProfile;
  /** 賽季的體重起點，還沒有任何量測時能量估算用它。 */
  baseline?: SeasonBaseline | null;
}

export interface BattleReport {
  /** 體重、7 日平均、本週變化、體脂、淨體重、生日目標日預估。 */
  trend: WeightTrend;
  countdown: Countdown;
  /** BMR／TDEE／建議攝取。全部是估算值，不是實測消耗。 */
  energy: EnergyGuide;
  /** 最近 14 天的達標率。 */
  compliance: ComplianceRates;
  assessment: StatusAssessment;
  /** 當天的 Rank。 */
  rank: QuestRank;
  /** 當天步數。尚未同步是 null。 */
  steps: number | null;
}

/** 戰報的達標率一律看最近 14 天。 */
export const REPORT_COMPLIANCE_DAYS = 14;

export function battleReport({
  measurements, logs, healthMetrics, today, profile = { ...DEFAULT_ENERGY_PROFILE }, baseline = null
}: BattleReportInput): BattleReport {
  const todayHealth = healthMetrics[today] || {};
  const steps = todayHealth.steps_count;

  return {
    trend: weightTrend(measurements, today),
    countdown: countdownInfo(today),
    energy: energyGuideFor({ measurements, today, date: today, profile, baseline }),
    compliance: compliance({ logs, healthMetrics, today }, REPORT_COMPLIANCE_DAYS),
    assessment: statusAssessment(measurements, today),
    rank: questRank(dayCompletion(logs[today] || {}, todayHealth)),
    steps: Number.isFinite(steps as number) ? (steps as number) : null
  };
}
